import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Checkbox, ComboBox, InputWithLabel, Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { dimensionUnits, userPermissions, weightUnits } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import usePackagingDetail from './hooks/usePackagingDetail/usePackagingDetail'
import PackagingDetailConfigurationSkeleton from './PackagingDetailConfigurationSkeleton/PackagingDetailConfigurationSkeleton'
import PackagingTypeSelector from './PackagingTypeSelector/PackagingTypeSelector'
import * as Styles from './styles'

const PackagingDetailConfiguration = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { id } = useParams()
  const { hasPermission } = useUserPermissions()
  const isEditable = hasPermission(userPermissions.editLogistics)

  const {
    data,
    onChange,
    hasChanges,
    isFormValid,
    onSaveChanges,
    creatingPackaging,
    editingPackaging,
    fetchingPackaging,
    resetData,
  } = usePackagingDetail({
    id,
    storeId: info?.selectedStore?.id,
    setToastInfo,
  })

  const dimensionUnitMenuItems = dimensionUnits.map(({ label, value }) => ({
    label: t(label),
    value,
  }))

  const weightUnitMenuItems = weightUnits.map(({ label, value }) => ({
    label: t(label),
    value,
  }))

  if (fetchingPackaging) return <PackagingDetailConfigurationSkeleton />

  return (
    <>
      <Styles.MainContainer>
        {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
        <Styles.SectionContainer>
          <Text type="titleS">{t('Tipo de paquete')}</Text>
          <Styles.RowContainer>
            <InputWithLabel
              label={t('Nombre del paquete')}
              value={data?.name}
              onChange={e => onChange('name', e.target.value)}
              disabled={!isEditable}
              fullWidth
            />
            <PackagingTypeSelector
              value={data?.type}
              onChange={value => onChange('type', value)}
              disabled={!isEditable}
            />
          </Styles.RowContainer>
        </Styles.SectionContainer>
        <Styles.SectionContainer>
          <Text type="titleS">{t('Dimensiones')}</Text>
          <Styles.DimensionsContainer>
            <InputWithLabel
              label={t('Largo')}
              value={data?.length}
              onChange={e => onChange('length', e.target.value)}
              disabled={!isEditable}
              fullWidth
              type="number"
            />
            <InputWithLabel
              label={t('Ancho')}
              value={data?.width}
              onChange={e => onChange('width', e.target.value)}
              disabled={!isEditable}
              fullWidth
              type="number"
            />
            <InputWithLabel
              label={t('Alto')}
              value={data?.height}
              onChange={e => onChange('height', e.target.value)}
              disabled={!isEditable}
              fullWidth
              type="number"
            />
            <ComboBox
              label={t('Unidad')}
              menuItems={dimensionUnitMenuItems}
              value={data?.dimension_unit || dimensionUnits[0].value}
              handleChange={e => onChange('dimension_unit', e.target.value)}
              disabled={!isEditable}
              fullWidth
            />
          </Styles.DimensionsContainer>
        </Styles.SectionContainer>
        <Styles.SectionContainer>
          <Text type="titleS">{t('Peso')}</Text>
          <Styles.RowContainer>
            <InputWithLabel
              label={t('Peso en vacío')}
              value={data?.weight}
              onChange={e => onChange('weight', e.target.value)}
              disabled={!isEditable}
              fullWidth
              type="number"
            />
            <ComboBox
              label={t('Unidad')}
              menuItems={weightUnitMenuItems}
              value={data?.weight_unit || weightUnits[0].value}
              handleChange={e => onChange('weight_unit', e.target.value)}
              disabled={!isEditable}
              fullWidth
            />
          </Styles.RowContainer>
        </Styles.SectionContainer>
        <Checkbox
          checked={data?.is_default}
          onChange={e => onChange('is_default', e.target.checked)}
          label={t('Seleccionar embalaje como predeterminado')}
          disabled={!isEditable}
        />
      </Styles.MainContainer>
      <Styles.ActionsContainer>
        <SaveChangesButtons
          loading={creatingPackaging || editingPackaging}
          disabled={!isFormValid() || creatingPackaging || editingPackaging}
          onSave={onSaveChanges}
          hasChanges={isEditable && hasChanges}
          onRevertChanges={resetData}
        />
      </Styles.ActionsContainer>
    </>
  )
}

export default PackagingDetailConfiguration
